/* eslint-disable react/no-danger */
import React from 'react';
import { t } from 'i18next';
import cn from 'classnames';
import { func, oneOf, string } from 'prop-types';

import Field from '~components/Field';
import InputLabel from '~components/InputLabel';
import CustomButton from '~components/CustomButton';
import SectionTitle from '~components/SectionTitle';
import { ReactComponent as Chevron } from '~assets/icons/chevron.svg';

import { FIELDS } from './constants';
import { ReactComponent as CancelCoupon } from './assets/cancel_coupon.svg';
import styles from './styles.module.scss';
import GiftCardBalance from './components/GiftCardBalance';
import { useRedeemCode } from './hooks';
import { formatExtraMetadata } from './utils';
import { RedeemCouponProvider } from './components/context';
import { Container } from './components/Container';
import { Header } from './components/Header';
import { Content } from './components/Content';

const VARIANTS = { small: 'small', large: 'large' };

// eslint-disable-next-line complexity
function RedeemCoupon({
  variant = VARIANTS.large,
  title = t('ReedeemCode:sectionTitle'),
  description,
  setFieldValue,
  onRedeemCoupon = () => undefined
}) {
  const {
    redeemedCode,
    inputState,
    currentUser,
    disabledInput,
    disabledRedeemCode,
    displaySuccessMessage,
    handleChangeInput,
    handleClearCoupon,
    handleRedeemCode,
    openLoginModal,
    displayLoyaltyText,
    enabledCCForUnregUsers,
    sizesAndCrusts
  } = useRedeemCode({ setFieldValue, onRedeemCoupon });
  const { activeInput, error, code, activeCode } = inputState;

  return (
    <RedeemCouponProvider value={{ variant, halfWidth: displayLoyaltyText }}>
      <Container>
        <Header>
          {title ? (
            <SectionTitle
              hideSeparator
              className={styles.sectionTitle}
              title={title}
              titleClassName={styles.title}
            />
          ) : null}

          {description ? <p className={styles.loyaltyText}>{description}</p> : null}
        </Header>
        {disabledRedeemCode ? (
          <p className={styles.disabledMessage}>{t('ReedeemCode:disabledMessage')}</p>
        ) : (
          <Content>
            <div>
              <div
                className={cn(
                  `row ${currentUser ? 'start' : 'center'} ${styles.container} ${styles.containerCoupon}`,
                  {
                    [styles.disabledInput]: currentUser && disabledRedeemCode
                  }
                )}>
                {currentUser || enabledCCForUnregUsers ? (
                  <>
                    <Field
                      name={FIELDS.coupon}
                      component={InputLabel}
                      inputType="text"
                      className={styles.couponContainer}
                      placeholder={t('ReedeemCode:placeholder')}
                      labelContainerClassName={styles.inputLabel}
                      inputClassName={cn(styles.input, {
                        [styles.inputError]: error,
                        [styles.inputSuccess]: !error && redeemedCode?.ok
                      })}
                      showError={false}
                      onChange={handleChangeInput}
                      disabled={disabledInput}
                      autoComplete="off"
                      value={code}
                      sx={{
                        border: 'none',
                        margin: '0',
                        padding: '0',
                        '& fieldset': {
                          border: 'none'
                        },
                        '& input': {
                          paddingTop: '0',
                          paddingBottom: '0'
                        }
                      }}
                    />
                    {activeCode ? (
                      <div className={`column ${styles.cancelCouponButton}`} onClick={handleClearCoupon}>
                        <CancelCoupon className={styles.removeCodeButton} />
                      </div>
                    ) : (
                      <CustomButton
                        id="add-coupon-button"
                        type="button"
                        text={t('ReedeemCode:add')}
                        onClick={handleRedeemCode}
                        disabled={activeInput}
                        btnClassType="outlined"
                        textCapitalize={false}
                        size="small"
                        className={styles.addCouponButton}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <CustomButton type="button" className={styles.guestCouponButton} onClick={openLoginModal}>
                      <p className={styles.guestCoupon}>{t('ReedeemCode:guestButtonInitSesion')}</p>
                      <Chevron className={styles.chevronIcon} />
                    </CustomButton>
                  </>
                )}
              </div>
              {(error || displaySuccessMessage) && (
                <div className={styles.message}>
                  {error && (
                    <>
                      <span
                        className={styles.messageError}
                        dangerouslySetInnerHTML={{
                          __html: t(`ReedeemCode:${redeemedCode?.errorMessage || 'errorMessage'}`, {
                            ...(redeemedCode?.extraMetadata &&
                              formatExtraMetadata(redeemedCode?.extraMetadata, sizesAndCrusts))
                          })
                        }}
                      />
                    </>
                  )}
                  {displaySuccessMessage ? (
                    <span className={styles.messageSuccess}>
                      {`${t('ReedeemCode:successMessage')} ${
                        redeemedCode?.isCampaign ? t('ReedeemCode:campaignConditions') : ''
                      }`}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
            {redeemedCode?.isGiftCard ? <GiftCardBalance /> : null}
          </Content>
        )}
      </Container>
    </RedeemCouponProvider>
  );
}

RedeemCoupon.propTypes = {
  description: string,
  setFieldValue: func,
  title: string,
  variant: oneOf(Object.values(VARIANTS)),
  onRedeemCoupon: func
};

export default RedeemCoupon;
